import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { BIZ } from "../lib/constants";

const STORAGE_KEY = "ls_age_verified";
const STORAGE_TS = "ls_age_verified_ts";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const isVerified = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        const ts = parseInt(localStorage.getItem(STORAGE_TS) || "0", 10);
        if (v !== "true" || !ts) return false;
        return Date.now() - ts < TTL_MS;
    } catch (_) {
        return false;
    }
};

export const AgeGate = () => {
    const [open, setOpen] = useState(() => !isVerified());
    const yesBtnRef = useRef(null);
    const dialogRef = useRef(null);

    // Focus trap & ESC disabled
    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";

        // Initial focus
        const t = setTimeout(() => yesBtnRef.current?.focus(), 50);

        const handleKey = (e) => {
            // ESC disabled
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            // Tab focus trap
            if (e.key === "Tab" && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll(
                    'button, [href], input, [tabindex]:not([tabindex="-1"])'
                );
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener("keydown", handleKey, true);
        return () => {
            clearTimeout(t);
            window.removeEventListener("keydown", handleKey, true);
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleEnter = () => {
        try {
            localStorage.setItem(STORAGE_KEY, "true");
            localStorage.setItem(STORAGE_TS, String(Date.now()));
        } catch (_) {}
        setOpen(false);
    };

    const handleUnder = () => {
        window.location.href = "https://www.responsibility.org/";
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="agegate-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="agegate-title"
                    aria-describedby="agegate-desc"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="age-gate-modal"
                >
                    <motion.div
                        ref={dialogRef}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="relative max-w-lg w-full"
                        style={{
                            background: "var(--ls-panel)",
                            border: "1px solid var(--ls-border-strong)",
                            borderRadius: 12,
                            padding: 40,
                            textAlign: "center",
                        }}
                    >
                        <div className="flex justify-center mb-8">
                            <Logo size={56} />
                        </div>

                        <span className="pill mb-6" style={{ marginBottom: 24 }}>
                            <span aria-hidden="true">●</span> 21+ ONLY
                        </span>

                        <h1
                            id="agegate-title"
                            className="font-display"
                            style={{
                                fontSize: 32,
                                fontWeight: 600,
                                marginTop: 24,
                                marginBottom: 12,
                                color: "var(--ls-text)",
                            }}
                            data-testid="age-gate-title"
                        >
                            Are you of legal drinking age?
                        </h1>
                        <p
                            id="agegate-desc"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 15,
                                lineHeight: 1.6,
                                marginBottom: 32,
                            }}
                        >
                            You must be <strong style={{ color: "var(--ls-gold)" }}>21 or older</strong> to
                            enter {BIZ.name}. Missouri state law requires
                            verification of age for the purchase of alcoholic
                            beverages.
                        </p>

                        <div
                            className="flex flex-col sm:flex-row gap-3 justify-center"
                            style={{ marginBottom: 24 }}
                        >
                            <button
                                ref={yesBtnRef}
                                onClick={handleEnter}
                                className="btn-primary"
                                data-testid="age-gate-confirm"
                                aria-label="Confirm I am 21 years of age or older"
                            >
                                I am 21 or older
                            </button>
                            <button
                                onClick={handleUnder}
                                className="btn-ghost"
                                data-testid="age-gate-deny"
                                aria-label="I am under 21, leave site"
                            >
                                I am under 21
                            </button>
                        </div>

                        <p
                            style={{
                                color: "var(--ls-text-muted)",
                                fontSize: 11,
                                lineHeight: 1.5,
                                letterSpacing: 0.02,
                                borderTop: "1px solid var(--ls-border)",
                                paddingTop: 20,
                                marginTop: 8,
                            }}
                        >
                            By entering this site you confirm that you are of
                            legal drinking age in your jurisdiction. We ID anyone
                            who appears under 40. Please drink responsibly.
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AgeGate;
